import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, Loader2, X } from "lucide-react";
import "./mlb-player-search.css";

const MLB_API = "https://statsapi.mlb.com/api/v1";

const searchMLBPlayers = async (query) => {
  const url = `${MLB_API}/people/search?names=${encodeURIComponent(query)}&sportId=1&hydrate=currentTeam`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("MLB API error");
  const data = await res.json();
  return (data.people || []).slice(0, 10);
};

export const MLBPlayerSearch = (props) => {
  const { selected, onSelect } = props;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const players = await searchMLBPlayers(q);
      setResults(players);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 350);
  };

  const handleSelect = (player) => {
    onSelect(player);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      clearTimeout(timer.current);
    };
  }, []);

  if (selected) {
    return (
      <div className="mps-selected">
        <div className="mps-selected-info">
          <div className="mps-selected-name">{selected.fullName}</div>
          <div className="mps-selected-meta">
            {selected.currentTeam?.name} · {selected.primaryPosition?.abbreviation}
          </div>
        </div>
        <button className="mps-clear-btn" onClick={() => onSelect(null)}>
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="mps-root" ref={ref}>
      <div className="mps-input-wrap">
        <input
          type="text"
          className="mps-input"
          value={query}
          onChange={handleChange}
          placeholder="Search MLB players"
          autoComplete="off"
        />
        <div className="mps-icon">
          {loading
            ? <div className="mps-spin"><Loader2 size={16} /></div>
            : <Search size={16} />
          }
        </div>
      </div>

      {open && results.length > 0 && (
        <div className="mps-dropdown">
          {results.map((player) => (
            <div
              key={player.id}
              className="mps-player-row"
              onClick={() => handleSelect(player)}
            >
              <div className="mps-player-name">{player.fullName}</div>
              <div className="mps-player-meta">
                {player.currentTeam?.name} · {player.primaryPosition?.abbreviation}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && !loading && query.length >= 2 && results.length === 0 && (
        <div className="mps-empty">No players found</div>
      )}
    </div>
  );
};
